import React from 'react'
import {
  Modal,
  PrimaryButton,
  SelectBrand,
  SelectCategory,
  SelectSku,
  SelectSubCategory,
  SearchFormSection,
  QuerySummary,
  SelectDivision,
} from 'components'
import { truncateWithSuffix } from 'utils/utilities'
import { compareArrays } from 'utils/forms'

//ADD OPTIONAL STATES IF REQUIRED
type ProductFilterSearchForm = {
  category: string[]
  subCategory: string[]
  brand: string[]
  sku: string[]
  skuName: string[]
  //Optional states
  reportName?: string
  division?: string[]
  depot?: string[]
  depotName?: string[]
  //Optional SKU reporting period
  reportingPeriod?: string
  startDate?: string
  endDate?: string
}

type ProductFilterItemProps = {
  label: string
  value: string[]
  noRightBorder?: boolean
}

const ProductFilterItem = (props: ProductFilterItemProps) => {
  const { label, value, noRightBorder } = props

  const displayValue: string = value ? (value.includes('all') ? 'All' : value.join(', ')) : ''

  return (
    <div key={`${label}-${value}`} className={`flex flex-1 flex-col pr-5 mr-4 ${noRightBorder ? '' : 'border-r-2'}`}>
      <span className={`text-lg font-semibold`}> {label} </span>
      <span className={`text-lg`} title={displayValue}>
        {truncateWithSuffix(displayValue, 40, '...')}
      </span>
    </div>
  )
}

type ProductFilterProps = {
  searchForm: ProductFilterSearchForm
  onChange(searchForm: ProductFilterSearchForm): void
  disabled?: string[]
}

export const ProductFilter: React.FC<ProductFilterProps> = (props: ProductFilterProps) => {
  const { searchForm, onChange, disabled } = props
  const [editOpen, setEditOpen] = React.useState<boolean>(false)
  const [editSearchForm, setEditSearchForm] = React.useState<ProductFilterSearchForm>(searchForm)

  const isSubmitEnabled =
    (!!editSearchForm.category.length && !compareArrays(searchForm.category, editSearchForm.category)) ||
    (!!editSearchForm.subCategory.length && !compareArrays(searchForm.subCategory, editSearchForm.subCategory)) ||
    (!!editSearchForm.brand.length && !compareArrays(searchForm.brand, editSearchForm.brand)) ||
    (!!editSearchForm.sku.length && !compareArrays(searchForm.sku, editSearchForm.sku)) ||
    (editSearchForm.division &&
      !!editSearchForm.division.length &&
      !compareArrays(searchForm.division ?? [], editSearchForm.division))

  const isResetEnabled =
    !editSearchForm.category.includes('all') ||
    !editSearchForm.subCategory.includes('all') ||
    !editSearchForm.brand.includes('all') ||
    !editSearchForm.sku.includes('all') ||
    (editSearchForm.division && !editSearchForm.division.includes('all'))

  const filterQuerySummary = () => {
    const data = [
      { header: 'Category', value: editSearchForm.category.join(', ') },
      {
        header: 'Sub-category',
        value: editSearchForm.subCategory.join(', '),
      },
      { header: 'Brand', value: editSearchForm.brand.join(', ') },
      { header: 'SKU', value: editSearchForm.skuName.join(', ') },
    ]

    if (editSearchForm.depotName && !editSearchForm.depotName.includes('all'))
      data.unshift({
        header: 'Depot',
        value: editSearchForm.depotName.join(', '),
      })
    if (editSearchForm.division)
      data.unshift({
        header: 'Division',
        value: editSearchForm.division.join(', '),
      })

    return data
  }

  const openFilterModal = () => {
    setEditSearchForm(searchForm)
    setEditOpen(true)
  }

  const resetEditForm = () => {
    setEditSearchForm((prevState) => ({
      ...prevState,
      category: ['all'],
      subCategory: ['all'],
      brand: ['all'],
      sku: ['all'],
      skuName: ['all'],
      division: prevState.division ? ['all'] : prevState.division,
    }))
  }

  const applyEditForm = () => {
    //Exclude the SKU reporting period related data
    const { reportingPeriod, startDate, endDate, ...rest } = editSearchForm

    onChange(rest)
    setEditOpen(false)
  }

  return (
    <>
      <div data-testid="product-filter" className={`flex flex-1 flex-col w-full pt-5 pb-1 px-4 bg-gray-100 rounded-md`}>
        <div className={`flex flex-row items-center justify-between`}>
          <div className={`flex flex-col w-full sm:flex-row`}>
            {searchForm.division && <ProductFilterItem label={`Division`} value={searchForm.division} />}
            {searchForm.depotName && !searchForm.depotName.includes('all') && (
              <ProductFilterItem label={`Depot`} value={searchForm.depotName} />
            )}
            <ProductFilterItem label={`Category`} value={searchForm.category} />
            <ProductFilterItem label={`Sub-category`} value={searchForm.subCategory} />
            <ProductFilterItem label={`Brand`} value={searchForm.brand} />
            <ProductFilterItem label={`SKU`} value={searchForm.skuName} noRightBorder={true} />
          </div>
          <div className={`flex mr-5`}>
            <span
              data-testid={`product-filter-filters-button`}
              className={`text-lg font-semibold text-primary-tint underline cursor-pointer`}
              onClick={openFilterModal}
            >
              Filters
            </span>
          </div>
        </div>
        <div className={`flex flex-col items-center mt-4`}>
          <div className={`w-40 border-t border-gray-300`} />
          <button className={`mt-1 text-lg text-primary-tint underline`} onClick={openFilterModal}>
            Show more
          </button>
        </div>
      </div>
      <Modal
        testId={'product-filters-edit-modal'}
        title={`Filters`}
        maxWidth={`lg`}
        open={editOpen}
        onClose={() => {
          setEditSearchForm(searchForm)
          setEditOpen(false)
        }}
      >
        <div className={`my-10`}>
          <SearchFormSection>
            {editSearchForm.division && (
              <SelectDivision
                disabled={disabled?.includes('division')}
                divisionState={{
                  ...editSearchForm,
                  division: editSearchForm.division,
                }}
                onChange={(division, isDefaultAll) => {
                  setEditSearchForm((prevState) => ({
                    ...prevState,
                    division,
                    category: isDefaultAll ? prevState.category : ['all'],
                    subCategory: isDefaultAll ? prevState.subCategory : ['all'],
                    brand: isDefaultAll ? prevState.brand : ['all'],
                    sku: isDefaultAll ? prevState.sku : ['all'],
                    skuName: isDefaultAll ? prevState.skuName : ['all'],
                  }))
                }}
              />
            )}
            <SelectCategory
              disabled={disabled?.includes('category')}
              categoryState={editSearchForm}
              onChange={(category, isDefaultAll) => {
                setEditSearchForm((prevState) => ({
                  ...prevState,
                  category: category,
                  subCategory: isDefaultAll ? prevState.subCategory : ['all'],
                  brand: isDefaultAll ? prevState.brand : ['all'],
                  sku: isDefaultAll ? prevState.sku : ['all'],
                  skuName: isDefaultAll ? prevState.skuName : ['all'],
                }))
              }}
            />
            <SelectSubCategory
              disabled={disabled?.includes('subCategory')}
              subCategoryState={editSearchForm}
              onChange={(subCategory, isDefaultAll) => {
                setEditSearchForm((prevState) => ({
                  ...prevState,
                  subCategory: subCategory,
                  brand: isDefaultAll ? prevState.brand : ['all'],
                  sku: isDefaultAll ? prevState.sku : ['all'],
                  skuName: isDefaultAll ? prevState.skuName : ['all'],
                }))
              }}
            />
            <SelectBrand
              disabled={disabled?.includes('brand')}
              brandState={editSearchForm}
              onChange={(brand, isDefaultAll) => {
                setEditSearchForm((prevState) => ({
                  ...prevState,
                  brand: brand,
                  sku: isDefaultAll ? prevState.sku : ['all'],
                  skuName: isDefaultAll ? prevState.skuName : ['all'],
                }))
              }}
            />
            <SelectSku
              disabled={disabled?.includes('sku')}
              skuState={editSearchForm}
              onChange={(data, isDefaultAll) => {
                isDefaultAll
                  ? setEditSearchForm((prevState) => ({
                      ...prevState,
                      sku: data.sku,
                      skuName: data.skuName,
                    }))
                  : setEditSearchForm((prevState) => ({
                      ...prevState,
                      division: prevState.division
                        ? !data.division.length
                          ? prevState.division
                          : data.division
                        : undefined,
                      category: !data.category.length ? prevState.category : data.category,
                      subCategory: !data.subCategory.length ? prevState.subCategory : data.subCategory,
                      brand: !data.brand.length ? prevState.brand : data.brand,
                      sku: data.sku,
                      skuName: data.skuName,
                    }))
              }}
              buttonBelow={true}
            />
          </SearchFormSection>
        </div>
        <div className={`flex flex-row justify-center my-8 gap-5`}>
          <PrimaryButton buttonText={`Reset`} outlined disabled={!isResetEnabled} onClick={resetEditForm} />
          <PrimaryButton buttonText={`Apply`} disabled={!isSubmitEnabled} onClick={applyEditForm} />
        </div>
        <div>
          <QuerySummary data={filterQuerySummary()} maxHeight={`sm`} />
        </div>
      </Modal>
    </>
  )
}
